import React, { useEffect, useImperativeHandle, useState } from 'react'
import { IoCheckmarkCircleOutline, IoCheckmark } from 'react-icons/io5'
import { ImCancelCircle } from 'react-icons/im'
import { PiWarningBold } from 'react-icons/pi'
import { LiaTimesSolid } from 'react-icons/lia'
import { LuLoader } from 'react-icons/lu'
import { AiOutlineClose } from 'react-icons/ai'
import { FaCheck } from 'react-icons/fa6'
import { AiOutlineExclamationCircle } from 'react-icons/ai'
import PropTypes from 'prop-types'

// Move menuColor outside the component so it can be referenced in types
const menuColor = {
    successV2: {
        bg: 'bg-neutral-900',
        text: 'text-white',
        cancel: '#FFFFFF',
        icon: <FaCheck size={22} className="text-success-500" />,
    },
    success: {
        bg: 'bg-neutral-900',
        text: 'text-white',
        cancel: '#104F2C',
        icon: (
            <IoCheckmarkCircleOutline
                size={22}
                className="text-success-800"
                color="white"
            />
        ),
    },
    warning: {
        bg: 'bg-warning-100',
        text: 'text-warning-1000',
        cancel: '#4A3307',
        icon: <PiWarningBold size={22} className="text-typography-low" />,
    },
    errorV2: {
        bg: 'bg-neutral-900',
        text: 'text-typography-low-contrast',
        cancel: '#FFFFFF',
        icon: <AiOutlineClose size={22} className="text-error-600" />,
    },
    error: {
        bg: 'bg-error-100',
        text: 'text-error-600',
        cancel: '#970C13',
        icon: (
            <ImCancelCircle
                size={22}
                className="text-typography-low text-error-600"
            />
        ),
    },
    defaultLoading: {
        bg: 'bg-neutral-900',
        text: 'text-white',
        cancel: '#FFFFFF',
        icon: <LuLoader size={22} className="text-white" />,
    },
    defaultSuccess: {
        bg: 'bg-white/10',
        text: 'text-white',
        cancel: '#FFFFFF',
        icon: <IoCheckmark size={22} className="text-success-500" />,
    },
    defaultError: {
        bg: 'bg-neutral-900',
        text: 'text-white',
        cancel: '#FFFFFF',
        icon: <ImCancelCircle size={22} className="text-white" />,
    },
    errorV3: {
        bg: 'bg-error-600',
        text: 'text-white',
        cancel: '#FFFFFF',
        icon: (
            <AiOutlineExclamationCircle
                strokeWidth={10}
                size={22}
                className="text-white"
            />
        ),
    },
}

interface SnackbarProps {
    title: string
    description?: string
    state?: keyof typeof menuColor
    closeAction?: boolean
    time?: number
}

interface SnackbarHandle {
    showModal: (b: boolean) => void
}

export const Snackbar = React.forwardRef<SnackbarHandle, SnackbarProps>(
    (
        {
            title,
            description,
            state = 'success',
            closeAction = true,
            time = 2000,
        },
        ref
    ) => {
        const [active, setActive] = useState(false)

        useImperativeHandle(ref, () => ({
            showModal: (b: boolean) => {
                setActive(b)
            },
        }))

        useEffect(() => {
            if (time > 0) {
                const timeout = setTimeout(() => {
                    setActive(false)
                }, time)

                return () => {
                    clearTimeout(timeout)
                }
            }
        }, [active])

        return (
            <>
                {active && (
                    <div className="fixed z-[999]">
                        <div
                            className={`fixed bottom-14 right-4 inline-flex items-center px-4 py-2 gap-3 ${menuColor[state].bg} rounded-lg`}
                        >
                            {menuColor[state]?.icon}
                            <div className="flex flex-col">
                                <h2
                                    className={`${menuColor[state].text} font-semibold`}
                                >
                                    {title}
                                </h2>
                                <h2
                                    className={`${menuColor[state].text} font-normal text-sm`}
                                >
                                    {description}
                                </h2>
                            </div>
                            {closeAction && (
                                <button
                                    onClick={() => setActive(false)}
                                    className="p-1 rounded-full"
                                >
                                    <LiaTimesSolid
                                        color={menuColor[state].cancel}
                                    />
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </>
        )
    }
)

Snackbar.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    state: PropTypes.string,
    closeAction: PropTypes.bool,
    time: PropTypes.number,
}
